import {
  collection,
  query,
  where,
  addDoc,
  getDocs,
  deleteDoc,
  serverTimestamp,
  limit,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from './firebase';

// Collection reference
const followsRef = collection(db, 'userFollows');

// Follow a user
export const followUser = async (followerId, followingId) => {
  try {
    // Validate inputs
    if (!followerId || !followingId) {
      throw new Error('Both follower and following user IDs are required');
    }
    
    // Check if already following
    const alreadyFollowing = await checkIfFollowing(followerId, followingId);
    if (alreadyFollowing) {
      return { success: true, message: 'Already following this user' };
    }
    
    // Create the follow relationship
    const follow = {
      followerId,
      followingId,
      createdAt: serverTimestamp()
    };
    
    const docRef = await addDoc(followsRef, follow);
    return {
      id: docRef.id,
      ...follow,
      success: true
    };
  } catch (error) {
    console.error('Error following user:', error);
    throw error;
  }
};

// Unfollow a user
export const unfollowUser = async (followerId, followingId) => {
  try {
    // Validate inputs
    if (!followerId || !followingId) {
      throw new Error('Both follower and following user IDs are required');
    }
    
    // Find the follow document
    const followQuery = query(
      followsRef,
      where('followerId', '==', followerId),
      where('followingId', '==', followingId),
      limit(1)
    );
    
    const followSnapshot = await getDocs(followQuery);
    
    if (followSnapshot.empty) {
      return { success: false, message: 'Not following this user' };
    }
    
    // Delete the follow relationship
    await deleteDoc(followSnapshot.docs[0].ref);
    
    return {
      success: true,
      message: 'Successfully unfollowed user'
    };
  } catch (error) {
    console.error('Error unfollowing user:', error);
    throw error;
  }
};

// Check if a user is following another user
export const checkIfFollowing = async (followerId, followingId) => {
  try {
    const followQuery = query(
      followsRef,
      where('followerId', '==', followerId),
      where('followingId', '==', followingId),
      limit(1)
    );
    
    const followSnapshot = await getDocs(followQuery);
    
    return !followSnapshot.empty;
  } catch (error) {
    console.error('Error checking follow status:', error);
    return false;
  }
};

// Get followers of a user
export const getFollowers = async (userId, page = 1, pageSize = 20) => {
  try {
    const followersQuery = query(
      followsRef,
      where('followingId', '==', userId),
      limit(pageSize)
    );
    
    const followersSnapshot = await getDocs(followersQuery);
    
    if (followersSnapshot.empty) {
      return { followers: [], hasMore: false };
    }
    
    const followers = [];
    followersSnapshot.forEach(doc => {
      followers.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return {
      followers,
      hasMore: followers.length === pageSize,
      page
    };
  } catch (error) {
    console.error('Error getting followers:', error);
    throw error;
  }
};

// Get users that a user is following
export const getFollowing = async (userId, page = 1, pageSize = 20) => {
  try {
    const followingQuery = query(
      followsRef,
      where('followerId', '==', userId),
      limit(pageSize)
    );
    
    const followingSnapshot = await getDocs(followingQuery);
    
    if (followingSnapshot.empty) {
      return { following: [], hasMore: false };
    }
    
    const following = [];
    followingSnapshot.forEach(doc => {
      following.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return {
      following,
      hasMore: following.length === pageSize,
      page
    };
  } catch (error) {
    console.error('Error getting following:', error);
    throw error;
  }
};

// Get follower count for a user
export const getFollowerCount = async (userId) => {
  try {
    const followersQuery = query(
      followsRef,
      where('followingId', '==', userId)
    );
    
    const followersSnapshot = await getDocs(followersQuery);
    
    return followersSnapshot.size;
  } catch (error) {
    console.error('Error getting follower count:', error);
    return 0;
  }
};

// Get following count for a user
export const getFollowingCount = async (userId) => {
  try {
    const followingQuery = query(
      followsRef,
      where('followerId', '==', userId)
    );
    
    const followingSnapshot = await getDocs(followingQuery);
    
    return followingSnapshot.size;
  } catch (error) {
    console.error('Error getting following count:', error);
    return 0;
  }
};

// Get detailed list of users that a user is following (with user data)
export const getFollowingList = async (userId, maxResults = 50) => {
  try {
    console.log('Getting following list for user:', userId);
    
    // First get the follow relationships
    const followingQuery = query(
      followsRef,
      where('followerId', '==', userId),
      limit(maxResults)
    );
    
    const followingSnapshot = await getDocs(followingQuery);
    
    if (followingSnapshot.empty) {
      console.log('No following relationships found');
      return [];
    }
    
    const followingUsers = [];
    
    // For each follow relationship, get the user details
    for (const followDoc of followingSnapshot.docs) {
      const followData = followDoc.data();
      const followingUserId = followData.followingId;
      
      try {
        // Get user data from users collection
        const userDocRef = doc(db, 'users', followingUserId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          followingUsers.push({
            uid: followingUserId,
            displayName: userData.displayName || userData.username || 'Anonymous',
            username: userData.username,
            photoURL: userData.photoURL || null,
            bio: userData.bio || '',
            followedAt: followData.createdAt
          });
        } else {
          console.warn('User not found:', followingUserId);
        }
      } catch (userError) {
        console.error('Error fetching user data for:', followingUserId, userError);
      }
    }
    
    console.log('Found following users:', followingUsers);
    return followingUsers;
  } catch (error) {
    console.error('Error getting following list:', error);
    return [];
  }
};